import React, { useEffect, useRef, useState } from "react";

import {
  isNullOrUndefined,
  isValidNonnegativeNumber,
  queryIsLoading,
  useGetDairyFeedAdditives,
  useGetFootprintingDairyFeedAdditive,
  useGetFootprintingDairyPrimaryDataById,
  useKeyedFormList,
  useMutateFootprintingDairyFeedAdditive,
  useMutateFootprintingDairyPrimaryData,
  useTranslation,
} from "utils";

import {
  DAIRY_PRACTICE_SLUG,
  ENUM_DAIRY_FEED_ADDITIVE_UNITS,
  ENUM_FARM_FOOTPRINTING_DAIRY_FEED_ADDITIVES,
  FARM_FOOTPRINTING_DAIRY_FEED_ADDITIVES,
  UploadableFeedAdditive,
} from "models";

import {
  Button,
  Card,
  Flex,
  Form,
  FormInstance,
  Icon,
  IconButton,
  InputNumberWithUnitSelector,
  KlimRadioGroup,
  Option,
  PageSpin,
  Select,
  Typography,
} from "components";

interface FarmGeneralProps {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
}

const feedAdditivePlaceholder: UploadableFeedAdditive = {
  id: undefined,
  feedAdditiveId: "",
  amount: null,
  amountUnit: null,
};

const FeedAdditiveForm = ({ primaryDataId, readOnly, farmId, year }: FarmGeneralProps) => {
  const { t } = useTranslation(["footprintingDairy", "dairy"]);
  const formRef = useRef<FormInstance>(null);

  const [feedAdditiveActive, setFeedAdditiveActive] = useState<ENUM_FARM_FOOTPRINTING_DAIRY_FEED_ADDITIVES | null>(null);

  const dairyPrimaryDataFullQuery = useGetFootprintingDairyPrimaryDataById(primaryDataId ?? "");
  const { data: dairyPrimaryDataQuery } = dairyPrimaryDataFullQuery;
  const feedAdditivesFullQuery = useGetFootprintingDairyFeedAdditive(primaryDataId);
  const { data: feedAdditivesQuery } = feedAdditivesFullQuery;
  const { data: dairyFeedAdditivesQuery } = useGetDairyFeedAdditives();
  const isLoading = [dairyPrimaryDataFullQuery, feedAdditivesFullQuery].some(queryIsLoading);

  const keyedFeedAdditives = useKeyedFormList<UploadableFeedAdditive>(formRef, { isReady: !isLoading });

  const selectGroupName = "footprinting-dairy-feed-additive-select";

  const { mutate: mutateDairyFeedAdditives } = useMutateFootprintingDairyFeedAdditive(
    farmId,
    year,
    {
      footprintingFarmPrimaryDataId: primaryDataId ?? "",
      selectedFeedAdditive: keyedFeedAdditives.payload.filter((f) => !!f.feedAdditiveId),
    },
    primaryDataId
  );

  const { mutate: mutateDairyPrimaryData } = useMutateFootprintingDairyPrimaryData(
    farmId,
    year,
    {
      farmId,
      year,
      feedAdditiveActive,
    },
    primaryDataId
  );

  // Save on unmount
  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutateDairyPrimaryData({});
        mutateDairyFeedAdditives();
      }
    },
    [isLoading, mutateDairyPrimaryData, mutateDairyFeedAdditives, readOnly]
  );

  // Load feed additives data
  useEffect(() => {
    if (feedAdditivesQuery) {
      keyedFeedAdditives.actions.hydrate(
        feedAdditivesQuery.length
          ? feedAdditivesQuery.map((f) => ({
              id: f.id,
              feedAdditiveId: f.feedAdditiveId ?? "",
              amount: f.amount ?? null,
              amountUnit: f.amountUnit ?? null,
            }))
          : [feedAdditivePlaceholder]
      );
    }
  }, [feedAdditivesQuery, keyedFeedAdditives.actions]);

  // Load primary data
  useEffect(() => {
    if (dairyPrimaryDataQuery) {
      setFeedAdditiveActive(dairyPrimaryDataQuery.feedAdditiveActive ?? null);

      formRef.current?.setFieldsValue({
        feedAdditiveActive: dairyPrimaryDataQuery.feedAdditiveActive ?? null,
      });

      if (dairyPrimaryDataQuery.feedAdditiveActive !== null && dairyPrimaryDataQuery.feedAdditiveActive !== undefined) {
        keyedFeedAdditives.actions.requestRehydrationValidation();
      }
    }
  }, [dairyPrimaryDataQuery, keyedFeedAdditives.actions]);

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <Card border dataTestId="footprinting-dairy-feed-additives-form" className="padding-100">
      <Form ref={formRef} preserve={false}>
        <Flex vertical gap={16}>
          <Flex align="center" gap={16} justify="space-between" className="padding-100">
            <Typography variant="paragraph-m" style={{ maxWidth: "60%" }}>
              {t("dairyFeedAdditivesForm.feedAdditivesQuestion")}
              <span className="required-asterisk">*</span>
            </Typography>
            <KlimRadioGroup
              required
              name="feedAdditiveActive"
              dataTestId="footprinting-dairy-feed-additives-radio"
              disabled={readOnly}
              vertical={false}
              type="tertiary"
              size="large"
              onChange={({ target }) => {
                setFeedAdditiveActive(target.value);
                if (target.value === FARM_FOOTPRINTING_DAIRY_FEED_ADDITIVES.YES && keyedFeedAdditives.entries.length === 0) {
                  keyedFeedAdditives.actions.add(feedAdditivePlaceholder);
                }
              }}
              value={feedAdditiveActive}
              defaultValue={feedAdditiveActive}
              optionType="button"
              options={[FARM_FOOTPRINTING_DAIRY_FEED_ADDITIVES.YES, FARM_FOOTPRINTING_DAIRY_FEED_ADDITIVES.NO].map((feedAdditiveActive) => ({
                label: t(`dairyFeedAdditivesForm.feedAdditiveOptions.${feedAdditiveActive}`),
                value: feedAdditiveActive,
              }))}
            />
          </Flex>
          {feedAdditiveActive === FARM_FOOTPRINTING_DAIRY_FEED_ADDITIVES.YES &&
            keyedFeedAdditives.entries.map((additive, index) => {
              const selectGroupWithIndex = `${selectGroupName}-${index}`;

              return (
                <Form.Item key={additive.clientKey} className="padding-left-100 padding-right-100 padding-bottom-0 padding-top-75">
                  <Flex vertical gap={16}>
                    <Flex justify="space-between" align="center" vertical={false}>
                      <Typography variant="h5" style={{ flex: 1 }}>
                        {t("dairyFeedAdditivesForm.additiveNumber", { count: index + 1 })}
                      </Typography>
                      <IconButton
                        type="tertiary"
                        disabled={readOnly}
                        dataTestId={`delete-feed-additive-${index}`}
                        size="small"
                        iconName="tabler/trash"
                        onClick={() => {
                          keyedFeedAdditives.actions.remove(additive.clientKey);
                          if (keyedFeedAdditives.entries.length === 1) setFeedAdditiveActive(null);
                        }}
                      />
                    </Flex>
                    <Select
                      disabled={readOnly}
                      name={`${selectGroupName}-${additive.clientKey}`}
                      dataTestId={selectGroupWithIndex}
                      label={t("dairyFeedAdditivesForm.question.feedAdditiveTypeQuestionInput")}
                      rules={[{ required: true, message: "" }]}
                      initialValue={dairyFeedAdditivesQuery?.find((f) => f.id === additive.feedAdditiveId)?.name || ""}
                      value={dairyFeedAdditivesQuery?.find((f) => f.id === additive.feedAdditiveId)?.name || ""}
                      onChange={(v) => {
                        const matchingPractice = dairyFeedAdditivesQuery?.find((f) => f.name === v);
                        keyedFeedAdditives.actions.update(additive.clientKey, { feedAdditiveId: matchingPractice ? matchingPractice.id : "" });
                      }}
                      dropdownStyle={{ zIndex: 10010 }} // The modal presenting us has a z-index of 10k
                    >
                      {(dairyFeedAdditivesQuery ?? []).map((dairyFeedAdditive) => {
                        // Check if this additive ID is already used in another item
                        const isAlreadyUsed = keyedFeedAdditives.entries.some(
                          (a) => a.feedAdditiveId === dairyFeedAdditive.id && a.feedAdditiveId !== additive.feedAdditiveId
                        );
                        return (
                          <Option
                            disabled={readOnly || isAlreadyUsed}
                            key={`${selectGroupWithIndex}-${dairyFeedAdditive.id}`}
                            data-testid={`${selectGroupWithIndex}-${dairyFeedAdditive.id}`}
                            value={dairyFeedAdditive.name}
                          >
                            {t(`${DAIRY_PRACTICE_SLUG.FEED_ADDITIVES}.${dairyFeedAdditive.translationSlug}`, { ns: "dairy" }) || dairyFeedAdditive.name}
                          </Option>
                        );
                      })}
                    </Select>

                    <InputNumberWithUnitSelector
                      key={`amount-${additive.clientKey}-${additive.feedAdditiveId}`}
                      className="floating-label"
                      disabled={readOnly}
                      dataTestId={`footprinting-dairy-feed-additive-amount-input-${index}`}
                      name={`amountApplied-${additive.clientKey}`}
                      label={t("dairyFeedAdditivesForm.question.amountAppliedQuestionInput")}
                      unitLabel={t("unit", { ns: "footprinting" })}
                      units={["kilograms", "tonnes", "cubic_meters", "liters"]}
                      value={additive.amount}
                      initialValue={additive.amount}
                      unitValue={additive.amountUnit}
                      onChange={(v, unit) =>
                        keyedFeedAdditives.actions.update(additive.clientKey, { amount: v, amountUnit: unit as ENUM_DAIRY_FEED_ADDITIVE_UNITS })
                      }
                      validateFirst
                      rules={[
                        {
                          required: true,
                          validator: (_: unknown, v: number) => {
                            if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                              return Promise.reject(new Error(t("dairyFeedAdditivesForm.error.amountAppliedError")));
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    />
                  </Flex>
                </Form.Item>
              );
            })}
          {feedAdditiveActive === FARM_FOOTPRINTING_DAIRY_FEED_ADDITIVES.YES && (
            <Button
              type="text"
              dataTestId="add-feed-additive-button"
              disabled={readOnly}
              onClick={() => {
                keyedFeedAdditives.actions.add(feedAdditivePlaceholder);
              }}
            >
              <Icon iconName="klim/plus" className="icon inline" /> {t("dairyFeedAdditivesForm.add")}
            </Button>
          )}
        </Flex>
      </Form>
    </Card>
  );
};

FeedAdditiveForm.Key = "dairy.feedAdditives";
export { FeedAdditiveForm };
